// src/services/dataPersistence/mapStateService.ts
// Service for managing map state separately from conversation flow

import { MapState, GeoPosition, LocationCard } from './types';
import { DataSyncService } from './dataSync';

export class MapStateService {
  private dataSyncService: DataSyncService;
  private lastSavedState: string = ''; // Hash of last saved state to prevent duplicate saves
  
  constructor(dataSyncService?: DataSyncService) {
    this.dataSyncService = dataSyncService || DataSyncService.getInstance();
  }
  

  private notifyDataUpdate() {
    window.dispatchEvent(new CustomEvent('tripper-data-updated'));
  }
  
  /**
   * Get current map state
   */
  public getMapState(): MapState {
    return this.dataSyncService.getMapState();
  }
  
  /**
   * Update map center position
   */
  public updateMapCenter(position: GeoPosition): void {
    // Create hash of position to check if it changed
    const positionHash = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
    
    // Get current map state
    const currentState = this.getMapState();
    const currentPositionHash = currentState.center ? 
      `${currentState.center.lat.toFixed(6)},${currentState.center.lng.toFixed(6)}` : '';
    
    // Skip update if position hasn't changed significantly
    if (positionHash === currentPositionHash) {
      return;
    }
    
    // Update the map state
    this.dataSyncService.updateMapState({
      ...currentState,
      center: position
    });
    this.notifyDataUpdate()
    console.log(`[MapStateService] Updated map center to ${positionHash}`);
  }
  
  /**
   * Update map zoom level
   */
  public updateMapZoom(zoom: number): void {
    // Get current map state
    const currentState = this.getMapState();
    
    // Skip update if zoom hasn't changed
    if (currentState.zoom === zoom) {
      return;
    }
    
    // Update the map state
    this.dataSyncService.updateMapState({
      ...currentState,
      zoom
    });
    this.notifyDataUpdate()
    console.log(`[MapStateService] Updated map zoom to ${zoom}`);
  }
  
  /**
   * Update visible location IDs
   */
  public updateVisibleLocations(locationIds: string[]): void {
    // Create hash of location IDs to check if they changed
    const locationsHash = locationIds.sort().join(',');
    
    // Skip update if locations haven't changed
    if (locationsHash === this.lastSavedState) {
      return;
    }
    
    // Get current map state
    const currentState = this.getMapState();
    
    // Update the map state
    this.dataSyncService.updateMapState({
      ...currentState,
      visibleLocationIds: locationIds
    });
    
    // Save hash of current state
    this.lastSavedState = locationsHash;
    this.notifyDataUpdate()
    console.log(`[MapStateService] Updated visible locations: ${locationIds.length} locations`);
  }
  
  /**
   * Set selected location
   * This updates the selected location without adding to conversation tree
   */
  public setSelectedLocation(locationId: string | null): void {
    // Get current map state
    const currentState = this.getMapState();
    
    // Skip update if selection hasn't changed
    if (currentState.selectedLocationId === locationId) {
      return;
    }
    
    // Update the map state
    this.dataSyncService.updateMapState({
      ...currentState,
      selectedLocationId: locationId || undefined
    });
    this.notifyDataUpdate()
    console.log(`[MapStateService] Set selected location to ${locationId || 'none'}`);
  }
  
  /**
   * Get the complete state with a single object
   */
  public updateMapState(mapState: Partial<MapState>): void {
    // Get current map state
    const currentState = this.getMapState();
    
    // Create hash of new state
    const newState = {
      ...currentState,
      ...mapState
    };
    
    // Create hash to check if state changed significantly
    const stateHash = JSON.stringify({
      center: newState.center ? 
        `${newState.center.lat.toFixed(6)},${newState.center.lng.toFixed(6)}` : null,
      zoom: newState.zoom,
      selectedLocationId: newState.selectedLocationId,
      visibleLocationIds: newState.visibleLocationIds?.length || 0
    });
    
    // Skip update if state hasn't changed
    if (stateHash === this.lastSavedState) {
      return;
    }
    
    // Update the map state
    this.dataSyncService.updateMapState(newState);
    this.notifyDataUpdate()
    // Save hash of current state
    this.lastSavedState = stateHash;
    
    console.log('[MapStateService] Updated map state');
  }
}